import { useEffect, useMemo, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { Link } from "wouter";
import {
  useGetMyToday,
  getGetMyTodayQueryKey,
  useCompleteLeadTask,
  useRescheduleLeadTask,
  getGetLeadsSummaryQueryKey,
  LeadStage,
  type Lead,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { maskMobileNumber } from "@/lib/format";
import {
  CheckCircle2,
  PhoneCall,
  AlertCircle,
  Phone,
  Clock,
  CalendarClock,
  CalendarDays,
  MapPin,
  CreditCard,
  UserPlus,
} from "lucide-react";

const STAGES = Object.values(LeadStage);

type Preset = "today" | "week" | "month" | "custom";

function pad(n: number) {
  return n.toString().padStart(2, "0");
}

function formatLocalForInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function priorityVariant(p: string): "default" | "secondary" | "destructive" | "outline" {
  if (p === "urgent") return "destructive";
  if (p === "high") return "default";
  if (p === "medium") return "secondary";
  return "outline";
}

type TaskType = "follow_up" | "callback" | "tour";

function TaskRow({
  lead,
  taskType,
  whenLabel,
  whenIso,
  slot,
  interactive,
}: {
  lead: Lead;
  taskType: TaskType;
  whenLabel: string;
  whenIso: string | null | undefined;
  slot?: string | null;
  interactive: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const complete = useCompleteLeadTask();
  const reschedule = useRescheduleLeadTask();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const initialCustom = useMemo(() => {
    const base = whenIso ? new Date(whenIso) : new Date();
    if (Number.isNaN(base.getTime()) || base.getTime() < Date.now()) {
      const inAnHour = new Date(Date.now() + 60 * 60 * 1000);
      return formatLocalForInput(inAnHour);
    }
    return formatLocalForInput(base);
  }, [whenIso]);
  const [customValue, setCustomValue] = useState(initialCustom);

  useEffect(() => {
    setCustomValue(initialCustom);
  }, [initialCustom]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetMyTodayQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetLeadsSummaryQueryKey() });
  };

  const doReschedule = (newDate: Date, label: string) => {
    if (taskType === "tour") return;
    reschedule.mutate(
      { id: lead.id, data: { taskType, newTime: newDate.toISOString() } },
      {
        onSuccess: () => {
          setPopoverOpen(false);
          toast({
            title: taskType === "follow_up" ? "Follow-up rescheduled" : "Callback rescheduled",
            description: `${lead.customerName} → ${label}`,
          });
          invalidate();
        },
        onError: () =>
          toast({
            variant: "destructive",
            title: "Could not reschedule",
            description: "Please try again in a moment.",
          }),
      },
    );
  };

  const snoozeMinutes = (mins: number) => {
    const base = whenIso ? new Date(whenIso) : new Date();
    const from = Number.isNaN(base.getTime()) || base.getTime() < Date.now() ? new Date() : base;
    const next = new Date(from.getTime() + mins * 60 * 1000);
    doReschedule(next, format(next, "MMM d, h:mm a"));
  };

  const tomorrowAt = (hour: number) => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    t.setHours(hour, 0, 0, 0);
    doReschedule(t, format(t, "MMM d, h:mm a"));
  };

  const submitCustom = () => {
    if (!customValue) return;
    const d = new Date(customValue);
    if (Number.isNaN(d.getTime())) {
      toast({ variant: "destructive", title: "Invalid date/time" });
      return;
    }
    doReschedule(d, format(d, "MMM d, h:mm a"));
  };

  const handleComplete = (stage?: LeadStage) => {
    if (taskType === "tour") return;
    complete.mutate(
      { id: lead.id, data: { taskType, ...(stage ? { stage } : {}) } },
      {
        onSuccess: () => {
          toast({
            title: taskType === "follow_up" ? "Follow-up completed" : "Callback completed",
            description: `${lead.customerName}${stage ? ` — moved to ${stage.replace(/_/g, " ")}` : ""}`,
          });
          invalidate();
        },
        onError: () =>
          toast({
            variant: "destructive",
            title: "Could not complete task",
            description: "Please try again in a moment.",
          }),
      },
    );
  };

  const overdue = whenIso ? new Date(whenIso).getTime() < Date.now() : false;

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            href={`/leads/${lead.id}`}
            className="text-base font-semibold hover:underline truncate"
          >
            {lead.customerName}
          </Link>
          <Badge variant={priorityVariant(lead.priority)} className="capitalize">
            {lead.priority}
          </Badge>
          <Badge variant="outline" className="capitalize">
            {lead.stage.replace(/_/g, " ")}
          </Badge>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span className="flex items-center font-mono">
            <Phone className="mr-1 h-3.5 w-3.5" />
            {maskMobileNumber(lead.mobileNumber)}
          </span>
          <span className="flex items-center">
            <Clock className="mr-1 h-3.5 w-3.5" />
            <span className={interactive && overdue ? "text-destructive font-medium" : ""}>
              {whenLabel}
            </span>
          </span>
          {slot && <span>Slot: {slot}</span>}
        </div>
      </div>
      {interactive && taskType !== "tour" && (
        <div className="flex flex-wrap items-center gap-2">
          <Select onValueChange={(v) => handleComplete(v as LeadStage)}>
            <SelectTrigger className="w-[180px]" data-testid={`stage-select-${taskType}-${lead.id}`}>
              <SelectValue placeholder="Update stage…" />
            </SelectTrigger>
            <SelectContent>
              {STAGES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.replace(/_/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                disabled={reschedule.isPending}
                data-testid={`reschedule-${taskType}-${lead.id}`}
              >
                <CalendarClock className="mr-1 h-4 w-4" />
                Snooze
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="end">
              <div className="space-y-3">
                <div>
                  <p className="mb-2 text-sm font-medium">Snooze</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => snoozeMinutes(60)}
                      disabled={reschedule.isPending}
                      data-testid={`snooze-1h-${taskType}-${lead.id}`}
                    >
                      +1 hour
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => snoozeMinutes(180)}
                      disabled={reschedule.isPending}
                      data-testid={`snooze-3h-${taskType}-${lead.id}`}
                    >
                      +3 hours
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => tomorrowAt(10)}
                      disabled={reschedule.isPending}
                      data-testid={`snooze-tomorrow-${taskType}-${lead.id}`}
                    >
                      Tomorrow 10:00 AM
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`custom-${taskType}-${lead.id}`} className="text-sm font-medium">
                    Reschedule to
                  </Label>
                  <Input
                    id={`custom-${taskType}-${lead.id}`}
                    type="datetime-local"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    data-testid={`custom-input-${taskType}-${lead.id}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="w-full"
                    onClick={submitCustom}
                    disabled={reschedule.isPending || !customValue}
                    data-testid={`custom-submit-${taskType}-${lead.id}`}
                  >
                    Save new time
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Button
            size="sm"
            onClick={() => handleComplete()}
            disabled={complete.isPending}
            data-testid={`complete-${taskType}-${lead.id}`}
          >
            <CheckCircle2 className="mr-1 h-4 w-4" />
            Mark done
          </Button>
        </div>
      )}
    </div>
  );
}

function presetRange(preset: Preset, customFrom: string, customTo: string) {
  const today = new Date();
  if (preset === "today") {
    return { from: startOfDay(today), to: endOfDay(today) };
  }
  if (preset === "week") {
    const from = startOfDay(today);
    from.setDate(from.getDate() - 6);
    return { from, to: endOfDay(today) };
  }
  if (preset === "month") {
    const from = startOfDay(today);
    from.setDate(from.getDate() - 29);
    return { from, to: endOfDay(today) };
  }
  // custom
  const fromDate = customFrom ? new Date(`${customFrom}T00:00:00`) : startOfDay(today);
  const toDate = customTo ? new Date(`${customTo}T23:59:59.999`) : endOfDay(today);
  const from = Number.isNaN(fromDate.getTime()) ? startOfDay(today) : fromDate;
  const to = Number.isNaN(toDate.getTime()) ? endOfDay(today) : toDate;
  return { from, to };
}

function rangeLabel(preset: Preset, from: Date, to: Date): string {
  if (preset === "today") return format(from, "EEEE, MMM d");
  if (sameDay(from, to)) return format(from, "MMM d, yyyy");
  if (from.getFullYear() === to.getFullYear()) {
    return `${format(from, "MMM d")} – ${format(to, "MMM d, yyyy")}`;
  }
  return `${format(from, "MMM d, yyyy")} – ${format(to, "MMM d, yyyy")}`;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export default function CalendarPage() {
  const todayStr = formatDateInput(new Date());
  const [preset, setPreset] = useState<Preset>("today");
  const [customFrom, setCustomFrom] = useState(todayStr);
  const [customTo, setCustomTo] = useState(todayStr);

  const { from, to } = useMemo(
    () => presetRange(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );
  const fromIso = from.toISOString();
  const toIso = to.toISOString();

  const { data, isLoading } = useGetMyToday(
    { from: fromIso, to: toIso },
    {
      query: {
        queryKey: [...getGetMyTodayQueryKey({ from: fromIso, to: toIso })],
        refetchInterval: preset === "today" ? 60000 : false,
      },
    },
  );

  const interactive = preset === "today";
  const totalCount =
    (data?.followUps.length ?? 0) + (data?.callbacks.length ?? 0) + (data?.tours.length ?? 0);

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Calendar</h1>
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Calendar</h1>
        <p className="text-muted-foreground">
          {rangeLabel(preset, from, to)} · {totalCount} event{totalCount === 1 ? "" : "s"}
        </p>
      </div>

      <Tabs value={preset} onValueChange={(v) => setPreset(v as Preset)}>
        <TabsList>
          <TabsTrigger value="today" data-testid="calendar-preset-today">
            Today
          </TabsTrigger>
          <TabsTrigger value="week" data-testid="calendar-preset-week">
            This Week
          </TabsTrigger>
          <TabsTrigger value="month" data-testid="calendar-preset-month">
            This Month
          </TabsTrigger>
          <TabsTrigger value="custom" data-testid="calendar-preset-custom">
            Custom
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {preset === "custom" && (
        <div className="flex flex-wrap items-end gap-4 rounded-lg border p-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="calendar-from">From</Label>
            <Input
              id="calendar-from"
              type="date"
              className="w-[160px]"
              value={customFrom}
              max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              data-testid="calendar-from"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="calendar-to">To</Label>
            <Input
              id="calendar-to"
              type="date"
              className="w-[160px]"
              value={customTo}
              min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              data-testid="calendar-to"
            />
          </div>
        </div>
      )}

      <CalendarSummary
        callbackCount={data.callbacks.length}
        tourCount={data.tours.length}
        followUpCount={data.followUps.length}
        rangeLabel={rangeLabel(preset, from, to)}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5 text-blue-500" />
            Callbacks ({data.callbacks.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.callbacks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No callbacks in this range.</p>
          ) : (
            <div className="space-y-3">
              {data.callbacks.map((lead) => (
                <TaskRow
                  key={lead.id}
                  lead={lead}
                  taskType="callback"
                  whenIso={lead.callbackScheduledDate}
                  slot={lead.callbackScheduledSlot}
                  interactive={interactive}
                  whenLabel={
                    lead.callbackScheduledDate
                      ? interactive
                        ? `${format(new Date(lead.callbackScheduledDate), "h:mm a")} (${formatDistanceToNow(
                            new Date(lead.callbackScheduledDate),
                            { addSuffix: true },
                          )})`
                        : format(new Date(lead.callbackScheduledDate), "MMM d, h:mm a")
                      : "—"
                  }
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-orange-500" />
            Follow-ups ({data.followUps.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.followUps.length === 0 ? (
            <p className="text-sm text-muted-foreground">No follow-ups in this range.</p>
          ) : (
            <div className="space-y-3">
              {data.followUps.map((lead) => (
                <TaskRow
                  key={lead.id}
                  lead={lead}
                  taskType="follow_up"
                  whenIso={lead.nextFollowUpDate}
                  interactive={interactive}
                  whenLabel={
                    lead.nextFollowUpDate
                      ? interactive
                        ? formatDistanceToNow(new Date(lead.nextFollowUpDate), { addSuffix: true })
                        : format(new Date(lead.nextFollowUpDate), "MMM d, h:mm a")
                      : "—"
                  }
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-green-600" />
            Scheduled Tours ({data.tours.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.tours.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tours scheduled in this range.</p>
          ) : (
            <div className="space-y-3">
              {data.tours.map((lead) => {
                const tourDates = [lead.tourScheduledDate]
                  .filter((d): d is string => typeof d === "string" && d.length > 0)
                  .map((d) => new Date(d))
                  .filter((d) => !Number.isNaN(d.getTime()) && d >= from && d <= to);
                const earliest = tourDates.sort((a, b) => a.getTime() - b.getTime())[0] ?? null;
                return (
                  <TourRow
                    key={lead.id}
                    lead={lead}
                    when={earliest}
                    slot={lead.tourScheduledSlot}
                  />
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CalendarSummary({
  callbackCount,
  tourCount,
  followUpCount,
  rangeLabel,
}: {
  callbackCount: number;
  tourCount: number;
  followUpCount: number;
  rangeLabel: string;
}) {
  const stats = [
    { label: "Callbacks", value: callbackCount, icon: PhoneCall, color: "text-blue-500", pending: false },
    { label: "Scheduled Tours", value: tourCount, icon: CalendarDays, color: "text-green-600", pending: false },
    { label: "Follow-ups", value: followUpCount, icon: AlertCircle, color: "text-orange-500", pending: false },
    { label: "Payment Reminders", value: 0, icon: CreditCard, color: "text-purple-500", pending: true },
    { label: "New Onboardings", value: 0, icon: UserPlus, color: "text-teal-600", pending: true },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Calendar Summary</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">Events in {rangeLabel}</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="rounded-md border p-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon className={`h-3.5 w-3.5 ${s.color}`} />
                  {s.label}
                  {s.pending && (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                      Soon
                    </span>
                  )}
                </div>
                <div className={`text-2xl font-semibold ${s.pending ? "text-muted-foreground" : ""}`}>
                  {s.value}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function TourRow({
  lead,
  when,
  slot,
}: {
  lead: Lead;
  when: Date | null;
  slot: string | null | undefined;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            href={`/leads/${lead.id}`}
            className="text-base font-semibold hover:underline truncate"
          >
            {lead.customerName}
          </Link>
          <Badge variant={priorityVariant(lead.priority)} className="capitalize">
            {lead.priority}
          </Badge>
          <Badge variant="outline" className="capitalize">
            {lead.stage.replace(/_/g, " ")}
          </Badge>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span className="flex items-center font-mono">
            <Phone className="mr-1 h-3.5 w-3.5" />
            {maskMobileNumber(lead.mobileNumber)}
          </span>
          <span className="flex items-center">
            <CalendarDays className="mr-1 h-3.5 w-3.5" />
            {when ? format(when, "MMM d, h:mm a") : "—"}
          </span>
          {slot && <span>Slot: {slot}</span>}
          {lead.location && (
            <span className="flex items-center">
              <MapPin className="mr-1 h-3.5 w-3.5" />
              {lead.location}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
